// RU-TOKENTIMES TT-8: "Token last updated" and "Renew before" on a broker card, from the backend's stored-token times
// (GET /brokers: token_updated_at / token_expires_at, UTC ISO or null, with their sources). Nothing is guessed here:
// a missing or null value is shown as unknown. The relative time is re-computed in the browser every 30 s.
import { useEffect, useState } from "react";

const IST_OFFSET_MS = 330 * 60 * 1000;
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export const TICK_MS = 30 * 1000;
export const SOON_MS = 2 * 60 * 60 * 1000;

const SOURCE_TEXT = {
  recorded: "recorded when the token was saved",
  token_iat: "from the token's issued-at claim",
  token_exp: "from the token's expiry claim",
  "broker_rule:kite_0600_ist": "Kite rule: 06:00 IST after the recorded update",
};

export function parseTime(iso) {
  if (typeof iso !== "string" || !iso) return null;
  const t = Date.parse(iso);
  return Number.isFinite(t) ? t : null;
}

const pad = (n) => String(n).padStart(2, "0");
const istParts = (ms) => {
  const d = new Date(ms + IST_OFFSET_MS);
  return { y: d.getUTCFullYear(), mo: d.getUTCMonth(), d: d.getUTCDate(), h: d.getUTCHours(), mi: d.getUTCMinutes() };
};

/** "08:12 IST" today (IST), else "03 Nov 06:00 IST". */
export function formatIst(ms, nowMs) {
  const p = istParts(ms);
  const n = istParts(nowMs);
  const hm = `${pad(p.h)}:${pad(p.mi)} IST`;
  if (p.y === n.y && p.mo === n.mo && p.d === n.d) return hm;
  return `${pad(p.d)} ${MONTHS[p.mo]}${p.y === n.y ? "" : ` ${p.y}`} ${hm}`;
}

/** "3 h 12 m", "2 d 4 h", "45 m", "<1 m". */
export function span(ms) {
  const mins = Math.floor(Math.abs(ms) / 60000);
  if (mins < 1) return "<1 m";
  const d = Math.floor(mins / 1440);
  const h = Math.floor((mins % 1440) / 60);
  const m = mins % 60;
  if (d > 0) return `${d} d ${h} h`;
  if (h > 0) return `${h} h ${m} m`;
  return `${m} m`;
}

export function expiryTone(expMs, nowMs) {
  if (expMs == null) return "unknown";
  if (expMs <= nowMs) return "expired";
  if (expMs - nowMs < SOON_MS) return "soon";
  return "ok";
}

const TONE_CLASS = { expired: "txt-loss", soon: "txt-warn", ok: "txt-secondary", unknown: "txt-muted" };

export default function BrokerTokenTimes({ broker }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), TICK_MS);
    return () => clearInterval(t);
  }, []);

  const b = broker || {};
  const updMs = parseTime(b.token_updated_at);
  const expMs = parseTime(b.token_expires_at);
  const tone = expiryTone(expMs, now);
  const name = b.name || "broker";

  let renewText;
  if (expMs == null) renewText = "Renew before: expiry unknown";
  else if (tone === "expired") renewText = `Renew before ${formatIst(expMs, now)} · expired ${span(now - expMs)} ago`;
  else renewText = `Renew before ${formatIst(expMs, now)} · in ${span(expMs - now)}`;

  const updText = updMs == null
    ? "Token last updated unknown"
    : `Token last updated ${formatIst(updMs, now)} · ${updMs <= now ? `${span(now - updMs)} ago` : `in ${span(updMs - now)}`}`;

  return (
    <div className="flex flex-col gap-0.5 mt-2 text-xs font-mono" data-testid={`broker-token-times-${name}`}>
      <span
        className="txt-muted"
        data-testid={`broker-token-updated-${name}`}
        title={SOURCE_TEXT[b.token_updated_source] || "no stored time for this token"}
      >
        {updText}
      </span>
      <span
        className={TONE_CLASS[tone]}
        data-testid={`broker-token-expiry-${name}`}
        data-tone={tone}
        title={SOURCE_TEXT[b.token_expires_source] || "expiry cannot be derived from stored data"}
      >
        {renewText}
      </span>
    </div>
  );
}
